'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Image as ImageIcon, Check, ChevronRight } from 'lucide-react';
import Image from 'next/image';
import { useOutputStyles } from '@/shared/api/hooks/use-output-styles';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { cn } from '@/shared/lib/cn';
import { SettingsCard, PickerRow } from '@/shared/ui';
import { ProjectReferenceSheet } from '../scrollable-settings/ProjectReferenceSheet';
import { ThumbStrip } from '../scrollable-settings/ReferenceSummary';
import { useProjectReferences } from '@/features/project-reference/hooks/use-project-references';

export interface OutputContentProps {
  selectedOutputStyleId: number | null;
  onOutputStyleChange: (styleId: number | null) => void;
  // Layout variant — kept for API parity; both variants share the Apple
  // single-column preset-card idiom now.
  variant?: 'default' | 'compact';
  closeOnSelect?: () => void;
  // Project reference props (optional)
  projectId?: string;
  outputProjectReferenceIds?: string[] | null;
  onOutputProjectReferencesChange?: (ids: string[] | null) => void;
  onNavigateToReferences?: () => void;
}

export function OutputContent({
  selectedOutputStyleId,
  onOutputStyleChange,
  closeOnSelect,
  projectId,
  outputProjectReferenceIds,
  onOutputProjectReferencesChange,
  onNavigateToReferences,
}: OutputContentProps) {
  const t = useTranslations('render.settings.output');
  // "From reference" row label + the picker panel reuse the shared strings.
  const tRef = useTranslations('render.settings.projectReference');
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  // Output style is a UNIVERSAL base feature since 2026-06-14 (every plan incl.
  // free — EntitlementFeatureMap: output_style is no longer Designer-gated).
  // The custom-output reference picker and the artistic presets are therefore
  // available to ALL users; no ProBadge, no lock, no paywall route here.
  const { data: outputStyles = [], isLoading } = useOutputStyles();

  // Resolve the selected output reference's thumbnail for the row accessory.
  const { data: allRefs = [] } = useProjectReferences(projectId || '', {
    enabled: !!projectId,
  });
  const selectedRefImages = (outputProjectReferenceIds || [])
    .map((id) => allRefs.find((r) => r.id === id)?.attributes.file_url)
    .filter((url): url is string => !!url);

  const hasSelectedRefs = (outputProjectReferenceIds?.length ?? 0) > 0;
  // Photorealistic is selected when no style AND no refs
  const isPhotorealistic = selectedOutputStyleId === null && !hasSelectedRefs;
  // From reference is selected when refs are chosen
  const isFromReference = hasSelectedRefs;

  const handleSelect = (styleId: number | null) => {
    if (hasSelectedRefs) {
      onOutputProjectReferencesChange?.(null);
    }
    onOutputStyleChange(styleId);
    closeOnSelect?.();
  };

  const handleRefsSelectionChange = (ids: string[]) => {
    if (ids.length > 0) {
      onOutputStyleChange(null);
    }
    onOutputProjectReferencesChange?.(ids.length > 0 ? ids : null);
  };

  return (
    <div className="space-y-4">
      {/* Photorealistic — Apple parity: bordered pill row (xl corner radius,
          accent stroke when active). */}
      <button
        type="button"
        className={cn(
          'w-full flex items-center gap-3 py-3 px-4 rounded-2xl text-left',
          'transition-colors',
          isPhotorealistic
            ? 'border-2 border-primary'
            : 'border border-border/60'
        )}
        onClick={() => handleSelect(null)}
      >
        <div className="w-11 h-8 rounded bg-muted flex items-center justify-center flex-shrink-0">
          <ImageIcon
            className={cn(
              'w-4 h-4',
              isPhotorealistic ? 'text-primary' : 'text-muted-foreground'
            )}
          />
        </div>
        <span className="flex-1 text-card-body text-foreground">
          {t('photorealistic')}
        </span>
        {isPhotorealistic && (
          <Check
            className="w-5 h-5 text-primary flex-shrink-0"
            strokeWidth={2.5}
          />
        )}
      </button>

      {/* Custom style from references — a "From reference" drill-down to the
          single-select picker panel (grid + inline upload). Mirrors the aspect
          axes (Style/Materials/Colors). */}
      {projectId && onOutputProjectReferencesChange && (
        <>
          <SettingsCard>
            <PickerRow
              title={tRef('fromReference')}
              isSelected={isFromReference}
              onClick={() => setIsSheetOpen(true)}
              accessory={
                <div className="flex items-center gap-2">
                  {selectedRefImages.length > 0 && (
                    <ThumbStrip images={selectedRefImages} shape="circle" />
                  )}
                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
                </div>
              }
            />
          </SettingsCard>

          <ProjectReferenceSheet
            projectId={projectId}
            category="output"
            variant="output"
            selectedIds={outputProjectReferenceIds || []}
            onSelectionChange={handleRefsSelectionChange}
            maxSelection={1}
            open={isSheetOpen}
            onOpenChange={setIsSheetOpen}
            onNavigateToReferences={onNavigateToReferences}
          />
        </>
      )}

      {/* Artistic style preset cards — full-bleed preview + name + checkmark.
          Mirrors Apple `OutputStylePresetCard`: small leading thumbnails made
          every preset look identical on phones; full-bleed previews let users
          actually distinguish styles. */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(2)].map((_, i) => (
            <div
              key={i}
              className="rounded-xl overflow-hidden border border-border/60"
            >
              <Skeleton className="w-full aspect-[168/120] rounded-none" />
              <div className="p-3">
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        outputStyles.length > 0 && (
          <div className="space-y-3">
            <p className="text-form-label text-muted-foreground px-1">
              {t('artistic')}
            </p>
            {outputStyles.map((style) => {
              const isSelected = selectedOutputStyleId === parseInt(style.id);

              return (
                <button
                  type="button"
                  key={style.id}
                  className={cn(
                    'block w-full text-left rounded-xl overflow-hidden transition-colors',
                    'bg-muted/40',
                    isSelected
                      ? 'border-2 border-primary'
                      : 'border border-border/60'
                  )}
                  onClick={() =>
                    handleSelect(isSelected ? null : parseInt(style.id))
                  }
                >
                  <div className="relative w-full aspect-[168/120] bg-muted overflow-hidden">
                    <Image
                      src={style.attributes.preview_image_url}
                      alt={style.attributes.name}
                      fill
                      sizes="(max-width: 480px) 100vw, 480px"
                      className="object-cover"
                    />
                  </div>
                  <div className="px-4 py-2.5 flex items-center justify-between gap-2">
                    <span
                      className={cn(
                        'text-card-body',
                        isSelected
                          ? 'text-primary font-semibold'
                          : 'text-foreground'
                      )}
                    >
                      {style.attributes.name}
                    </span>
                    {isSelected && (
                      <Check
                        className="w-5 h-5 text-primary flex-shrink-0"
                        strokeWidth={2.5}
                      />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

// Helper to get subtitle for preview
export function getOutputSubtitle(
  selectedOutputStyleId: number | null,
  outputStyles: { id: string; attributes: { name: string } }[],
  t: (key: string) => string,
  outputProjectReferenceIds?: string[] | null
): string {
  // The render style is ONE choice with two mutually exclusive forms: a preset
  // (outputStyleId) OR custom file-based references. A render generated from a
  // file reference has outputStyleId === null but non-empty references — without
  // this it falls back to "Photo" instead of "From reference" (matches Apple/Android).
  if (outputProjectReferenceIds && outputProjectReferenceIds.length > 0) {
    return t('fromReference');
  }
  if (selectedOutputStyleId === null) {
    return t('photorealistic');
  }
  const selectedStyle = outputStyles.find(
    (s) => parseInt(s.id) === selectedOutputStyleId
  );
  return selectedStyle?.attributes.name || t('photorealistic');
}
